import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./About";
import { CTABanner } from "../components/Sections";
import { Button } from "../components/ui/button";
import { HelpCircle, ArrowRight } from "lucide-react";
import { steps } from "../mock";

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          const i = Number(e.target.dataset.index);
          setActiveStep(i);
        }
      });
    }, { rootMargin: "-40% 0px -40% 0px" });
    refs.current.forEach(el => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <main>
      <PageHeader title="How It Works" subtitle="OUR PROCESS" />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1588979355313-6711a095465f?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
              alt="How it works"
              className="rounded-3xl shadow-2xl w-full h-[480px] object-cover"
            />
          </div>
          <div>
            <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">ABOUT THE PROCESS</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-5">Improve and Maintain a <em>Good Credit Score</em> Today</h2>
            <p className="text-knight-navy/75 leading-relaxed mb-4">
              Having a good credit score is an accomplishment, but maintaining it can be much more challenging. Without understanding the items that affect your credit report, you would most likely make the same mistakes and lose your good score.
            </p>
            <p className="text-knight-navy/75 leading-relaxed">
              Avoid these setbacks with our help. We will teach you how to effectively maintain your credit and secure a stable financial future.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 subtle-gradient">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">STEP BY STEP</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy">Our Step-by-Step <em>Process</em></h2>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-slate-200" />
            <div
              className="absolute left-8 md:left-1/2 top-0 w-px bg-knight-gold transition-all duration-500"
              style={{ height: `${((activeStep + 1) / steps.length) * 100}%` }}
            />

            <div className="space-y-12">
              {steps.map((step, i) => (
                <div
                  key={i}
                  ref={el => (refs.current[i] = el)}
                  data-index={i}
                  className={`relative flex items-center gap-6 ${i % 2 === 0 ? "md:flex-row-reverse" : ""}`}
                >
                  <div className="flex-1 md:w-1/2">
                    <div className={`bg-white rounded-2xl p-6 shadow-md border-2 transition-all duration-500 ${activeStep === i ? "border-knight-gold shadow-xl scale-[1.02]" : "border-slate-100"}`}>
                      <p className="text-xs font-bold tracking-widest text-knight-gold mb-1">STEP {String(i + 1).padStart(2, "0")}</p>
                      <p className="text-knight-navy/85 leading-relaxed">{step}</p>
                    </div>
                  </div>
                  <div className="absolute left-8 md:left-1/2 -translate-x-1/2 z-10">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-lg border-4 transition-all duration-500 ${activeStep === i ? "bg-knight-gold text-knight-navy border-knight-navy scale-110" : "bg-white text-knight-navy border-slate-200"}`}>
                      {i + 1}
                    </div>
                  </div>
                  <div className="hidden md:block md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ CTA */}
        <div className="mt-16 text-center">
          <p className="text-knight-navy/70 mb-4 text-sm">Still have questions about how the process works?</p>
          <Link to="/pricing#faq">
            <Button size="lg" className="bg-knight-navy hover:bg-knight-navy-dark text-white font-bold btn-lift">
              <HelpCircle size={18} className="mr-2" /> Read Our FAQs <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <CTABanner />
    </main>
  );
};

export default HowItWorks;
